import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import { useEffect, useRef } from 'react'
import { Eyebrow } from '@/components/landing/eyebrow'
import { clamp, lerp, prefersReducedMotion } from '@/lib/motion'
import { addTicker } from '@/lib/ticker'
import { EVENT, VENUE_FACTS } from '@/constants/event'

export const CenterCourtSection: FC = (): ReactNode => {
    const sectionRef = useRef<HTMLElement>(null)
    const imageRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (prefersReducedMotion()) return

        return addTicker(() => {
            const section = sectionRef.current
            const image = imageRef.current
            if (!section || !image) return

            const rect = section.getBoundingClientRect()
            const viewportHeight = window.innerHeight
            if (rect.bottom < 0 || rect.top > viewportHeight) return

            const progress = clamp((viewportHeight - rect.top) / (viewportHeight + rect.height))
            image.style.transform = `translate3d(0, ${lerp(-6, 6, progress)}%, 0) scale(${lerp(1.18, 1.06, progress)})`
        })
    }, [])

    return (
        <section
            id='center-court'
            ref={sectionRef}
            aria-labelledby='center-court-title'
            className='relative isolate flex min-h-[115svh] flex-col justify-end overflow-hidden bg-ink text-cream'
        >
            <div ref={imageRef} className='absolute inset-0 -z-10 will-change-transform'>
                <Image
                    src='/images/center-court.jpg'
                    alt='Illustration of a packed pickleball stadium at sunset, a cobalt court ringed by stands, with desert mountains and palm trees behind'
                    fill
                    sizes='100vw'
                    className='object-cover object-[60%_center]'
                />
            </div>

            <div aria-hidden='true' className='absolute inset-0 -z-10 bg-linear-to-t from-ink via-ink/55 to-transparent' />
            <div aria-hidden='true' className='absolute inset-0 -z-10 bg-linear-to-r from-ink/70 via-transparent to-transparent' />

            <div className='mx-auto w-full max-w-[1440px] px-5 pb-12 pt-48 md:px-10 md:pb-16'>
                <Eyebrow className='text-lime'>Center court</Eyebrow>

                <h2 id='center-court-title' className='mt-5 max-w-4xl font-heading text-[clamp(3rem,8vw,7.5rem)] font-black uppercase leading-[0.86]'>
                    Four thousand seats.
                    <span className='serif-soft block font-serif font-normal normal-case italic text-lime'>every one near the kitchen.</span>
                </h2>

                <p className='mt-6 max-w-xl text-base leading-relaxed text-cream/85 md:text-lg'>
                    {`${EVENT.venue} sits at the edge of ${EVENT.city}, eighteen courts in a ring around one stadium. The sun goes down behind the far stands just as the night session starts.`}
                </p>

                <dl className='mt-12 grid grid-cols-2 gap-px overflow-hidden rounded-3xl border border-cream/15 bg-cream/15 md:mt-16 lg:grid-cols-4'>
                    {VENUE_FACTS.map((fact) => (
                        <div key={fact.label} className='flex flex-col gap-2 bg-ink/70 p-5 backdrop-blur-sm md:p-7'>
                            <dt className='order-2 text-sm leading-snug text-cream/75'>
                                {fact.label}
                            </dt>
                            <dd className='order-1 font-heading text-5xl font-black leading-none md:text-6xl'>
                                {fact.value}
                            </dd>
                        </div>
                    ))}
                </dl>
            </div>
        </section>
    )
}
